import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { DataProvider } from './contexts/DataContext';
import { BookmarkProvider } from './contexts/BookmarkContext';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import LoginRoute from './components/LoginRoute';
import Dashboard from './pages/Dashboard';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetail from './pages/ProjectDetail';
import ComponentDetail from './pages/ComponentDetail';
import PeoplePage from './pages/PeoplePage';
import PersonDetail from './pages/PersonDetail';
import BookmarksPage from './pages/BookmarksPage';
import ApiTestPage from './pages/ApiTestPage';
import DocumentationPage from './pages/DocumentationPage';
import ErrorBoundary from './components/ErrorBoundary';

const AppWrapper: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl floating"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-400/20 to-purple-500/20 rounded-full blur-3xl floating" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-blue-300/10 to-indigo-400/10 rounded-full blur-3xl floating" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative z-10">
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <Dashboard />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/projects" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <ProjectsPage />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/projects/:projectId" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <ProjectDetail />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/projects/:projectId/components/:componentName" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <ComponentDetail />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/people" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <PeoplePage />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/people/:personName" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <PersonDetail />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/bookmarks" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <BookmarksPage />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/api-test" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <ApiTestPage />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          <Route path="/documentation" element={
            <ProtectedRoute>
              <>
                <Header />
                <main className="container mx-auto px-4 py-8">
                  <ErrorBoundary>
                    <DocumentationPage />
                  </ErrorBoundary>
                </main>
              </>
            </ProtectedRoute>
          } />
          {/* Catch-all route for undefined paths */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <DataProvider>
        <BookmarkProvider>
          <Router>
            <AppWrapper />
          </Router>
        </BookmarkProvider>
      </DataProvider>
    </ThemeProvider>
  );
};

export default App;
